import React, { useEffect, useState, memo } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { Tooltip } from '../components/ui/Tooltip';
import { Plus, Filter, Download, Eye, Edit, Search, Wrench, CheckCircle2, X, Save, Mail, Calendar, Trash2, AlertCircle } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
const initialEquipmentData = Array.from({
  length: 50
}, (_, i) => ({
  id: String(i + 1),
  code: `EQP-${String(i + 1).padStart(3, '0')}`,
  name: i % 2 === 0 ? `Fonometro ${i}` : `Stampante ${i}`,
  type: i % 4 === 0 ? 'Strumento Misura' : i % 4 === 1 ? 'Hardware' : i % 4 === 2 ? 'Veicolo' : 'Attrezzatura',
  lastMaintenance: '2024-01-01',
  nextMaintenance: '2025-01-01',
  maintenanceType: i % 2 === 0 ? 'Ordinaria' : 'Straordinaria',
  responsible: i % 3 === 0 ? 'Alessio' : i % 3 === 1 ? 'Lorenzo' : 'Gian Franco',
  repeatability: i % 4 === 0 ? '3 mesi' : i % 4 === 1 ? '6 mesi' : i % 4 === 2 ? '1 anno' : '2 anni'
}));
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'name',
  label: 'Macchinario'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'lastMaintenance',
  label: 'Ultima manut.'
}, {
  key: 'nextMaintenance',
  label: 'Prox. manut.'
}, {
  key: 'responsible',
  label: 'Responsabile'
}, {
  key: 'maintenanceType',
  label: 'Tipologia manutenzione'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function EquipmentPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [maintenanceData, setMaintenanceData] = useState<any>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  // Custom state for "Creatable Select"
  const [equipmentTypes, setEquipmentTypes] = useState(['Strumento Misura', 'Hardware', 'Veicolo', 'Attrezzatura']);
  // View modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewItem, setViewItem] = useState<any>(null);
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialEquipmentData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'type', 'code']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleView = (item: any) => {
    setViewItem(item);
    setIsViewModalOpen(true);
  };
  const handleViewToEdit = () => {
    setIsViewModalOpen(false);
    handleEdit(viewItem);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    const nextCode = `EQP-${String(initialEquipmentData.length + 1).padStart(3, '0')}`;
    setFormData({
      code: nextCode,
      sendEmailReminder: true
    });
    setIsModalOpen(true);
  };
  const handleMaintenance = (item: any) => {
    setSelectedItem(item);
    setMaintenanceData({
      date: new Date().toISOString().split('T')[0],
      type: 'Ordinaria',
      sendEmailReminder: true
    });
    setIsMaintenanceModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
      addNotification('success', 'Eliminato', 'Macchinario eliminato con successo');
    }, 1500);
  };
  const handleSave = () => {
    // If a new type was entered, add it to the list
    if (formData.type && !equipmentTypes.includes(formData.type)) {
      setEquipmentTypes(prev => [...prev, formData.type]);
    }
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Macchinario salvato', 'Il macchinario è stato salvato con successo.');
    }, 1500);
  };
  const saveMaintenance = () => {
    addNotification('success', 'Manutenzione registrata', 'La manutenzione è stata salvata con successo.');
    setIsMaintenanceModalOpen(false);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-3762">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-3763">
        <div data-id="element-3764">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-3765">
            Macchinari e manutenzione
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-3766">
            Gestione asset aziendali e manutenzioni programmate.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-3767">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-3769" />} data-id="element-3768">
            Esporta
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-3771" />} data-id="element-3770">
            Nuovo macchinario
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-3772">
        <div className="p-4 flex flex-col gap-4" data-id="element-3773">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-3774">
            <div className="flex-1" data-id="element-3775">
              <Input placeholder="Cerca macchinario..." startContent={<Search className="h-4 w-4" data-id="element-3777" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-3776" />
            </div>
            <div className="flex gap-2" data-id="element-3778">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-3780" />} data-id="element-3779">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-3781">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="equipment" lockedColumns={['name']} data-id="element-3782" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-3783">
              <Select label="Tipologia manutenzione" value={filters.maintenanceType || ''} onChange={e => setFilter('maintenanceType', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Ordinaria',
            label: 'Ordinaria'
          }, {
            value: 'Straordinaria',
            label: 'Straordinaria'
          }]} data-id="element-3784" />
              <Select label="Tipologia Macchinario" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, ...equipmentTypes.map(t => ({
            value: t,
            label: t
          }))]} data-id="element-3785" />
              <div className="col-span-full flex justify-end" data-id="element-3786">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-3788" />} data-id="element-3787">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-3789">
        <div className="overflow-x-auto" data-id="element-3790">
          <table className="w-full text-left border-collapse table-auto" data-id="element-3791">
            <thead data-id="element-3792">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-3793">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-3794">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-3795">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-3796">
                  {visibleColumns.includes('code') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-3797">
                      {item.code}
                    </td>}
                  {visibleColumns.includes('name') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-3798">
                      {item.name}
                    </td>}
                  {visibleColumns.includes('type') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3799">
                      {item.type}
                    </td>}
                  {visibleColumns.includes('lastMaintenance') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3800">
                      {item.lastMaintenance}
                    </td>}
                  {visibleColumns.includes('nextMaintenance') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3801">
                      {item.nextMaintenance}
                    </td>}
                  {visibleColumns.includes('responsible') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-3802">
                      {item.responsible}
                    </td>}
                  {visibleColumns.includes('maintenanceType') && <td className="px-4 py-4" data-id="element-3803">
                      <Badge variant={item.maintenanceType === 'Ordinaria' ? 'info' : 'warning'} data-id="element-3804">
                        {item.maintenanceType}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-3805">
                      <div className="flex items-center gap-1" data-id="element-3806">
                        <Tooltip content="Visualizza macchinario" data-id="element-3807">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-3808">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-3809" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Registra manutenzione" data-id="element-3810">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleMaintenance(item)} data-id="element-3811">
                            <Wrench className="h-4 w-4 text-blue-500" data-id="element-3812" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Modifica macchinario" data-id="element-3813">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-3814">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-3815" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Elimina macchinario" data-id="element-3816">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-3817">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-3818" />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-3819">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-3820" />
        </div>
      </Card>

      {/* View/Consultation Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title={viewItem ? `Dettaglio macchinario — ${viewItem.code}` : ''} size="2xl" data-id="element-3821">
        {viewItem && <div className="space-y-8" data-id="element-3822">
            <section data-id="element-3823">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3824">
                <Wrench className="h-4 w-4" data-id="element-3825" /> Dettagli macchinario
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3826">
                <div data-id="element-3827">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-3828">
                    Codice
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-3829">
                    {viewItem.code}
                  </p>
                </div>
                <div data-id="element-3830">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-3831">
                    Tipologia
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-3832">
                    {viewItem.type || '-'}
                  </p>
                </div>
                <div className="col-span-full" data-id="element-3833">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-3834">
                    Nome
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-3835">
                    {viewItem.name || '-'}
                  </p>
                </div>
                {viewItem.description && <div className="col-span-full" data-id="element-3836">
                    <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-3837">
                      Descrizione
                    </label>
                    <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-3838">
                      {viewItem.description}
                    </p>
                  </div>}
                <div data-id="element-3839">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-3840">
                    Responsabile
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-3841">
                    {viewItem.responsible || '-'}
                  </p>
                </div>
                <div data-id="element-3842">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-3843">
                    Tipologia manutenzione
                  </label>
                  <Badge variant={viewItem.maintenanceType === 'Ordinaria' ? 'info' : 'warning'} data-id="element-3844">
                    {viewItem.maintenanceType}
                  </Badge>
                </div>
              </div>
            </section>

            <section data-id="element-3845">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3846">
                <Calendar className="h-4 w-4" data-id="element-3847" /> Manutenzione programmata
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3848">
                <div data-id="element-3849">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-3850">
                    Ultima manutenzione
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-3851">
                    {viewItem.lastMaintenance || '-'}
                  </p>
                </div>
                <div data-id="element-3852">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-3853">
                    Prossima scadenza
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-3854">
                    {viewItem.nextMaintenance || '-'}
                  </p>
                </div>
                <div data-id="element-3855">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-3856">
                    Ripetibilità
                  </label>
                  <p className="text-sm font-medium text-slate-900" data-id="element-3857">
                    {viewItem.repeatability || '-'}
                  </p>
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3858">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-3859">
                Chiudi
              </Button>
              <Button onClick={handleViewToEdit} startContent={<Edit className="h-4 w-4" data-id="element-3861" />} data-id="element-3860">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica macchinario' : 'Nuovo macchinario'} size="2xl" data-id="element-3862">
        <AnimatePresence mode="wait" data-id="element-3863">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-3864">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3865">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-3866" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3867">
                Macchinario salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3868">
              <div className="space-y-8" data-id="element-3869">
                <section data-id="element-3870">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3871">
                    <Wrench className="h-4 w-4" data-id="element-3872" /> Dettagli macchinario
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3873">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} placeholder="EQP-001" data-id="element-3874" />

                    {/* Custom Creatable Select Implementation */}
                    <div className="flex flex-col gap-1.5" data-id="element-3875">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-3876">
                        Tipologia <span className="text-red-500" data-id="element-3877">*</span>
                      </label>
                      <input list="equipment-types" className="block w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-seform-orange focus:bg-white focus:ring-0 focus:outline-none" value={formData.type || ''} onChange={e => setFormData({
                    ...formData,
                    type: e.target.value
                  })} placeholder="Seleziona o scrivi nuovo..." data-id="element-3878" />
                      <datalist id="equipment-types" data-id="element-3879">
                        {equipmentTypes.map(type => <option key={type} value={type} data-id="element-3880" />)}
                      </datalist>
                    </div>

                    <Input label="Nome" placeholder="Es. Fonometro" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required className="col-span-full" data-id="element-3881" />

                    <div className="col-span-full" data-id="element-3882">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} data-id="element-3883" />
                    </div>

                    <Input label="Responsabile" placeholder="Nome responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-3884" />

                    <Select label="Tipologia manutenzione" value={formData.maintenanceType || 'Ordinaria'} onChange={e => setFormData({
                  ...formData,
                  maintenanceType: e.target.value
                })} options={[{
                  value: 'Ordinaria',
                  label: 'Ordinaria'
                }, {
                  value: 'Straordinaria',
                  label: 'Straordinaria'
                }]} data-id="element-3885" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-3886"></div>

                <section data-id="element-3887">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3888">
                    <Calendar className="h-4 w-4" data-id="element-3889" /> Manutenzione programmata
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3890">
                    <DatePicker label="Prossima scadenza" value={formData.nextMaintenance || ''} onChange={e => setFormData({
                  ...formData,
                  nextMaintenance: e.target.value
                })} data-id="element-3891" />
                    <Select label="Ripetibilità" value={formData.repeatability || ''} onChange={e => setFormData({
                  ...formData,
                  repeatability: e.target.value
                })} options={[{
                  value: '3 mesi',
                  label: '3 mesi'
                }, {
                  value: '6 mesi',
                  label: '6 mesi'
                }, {
                  value: '1 anno',
                  label: '1 anno'
                }, {
                  value: '2 anni',
                  label: '2 anni'
                }]} data-id="element-3892" />
                    <div className="col-span-full flex items-center gap-2" data-id="element-3893">
                      <input type="checkbox" id="sendEmailReminder" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.sendEmailReminder || false} onChange={e => setFormData({
                    ...formData,
                    sendEmailReminder: e.target.checked
                  })} data-id="element-3894" />
                      <label htmlFor="sendEmailReminder" className="text-sm text-slate-700 flex items-center gap-1" data-id="element-3895">
                        <Mail className="h-4 w-4 text-slate-400" data-id="element-3896" />
                        Invia promemoria email alla scadenza
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3897">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-3898">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3900" />} data-id="element-3899">
                    Salva macchinario
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Maintenance Modal */}
      <Modal isOpen={isMaintenanceModalOpen} onClose={() => setIsMaintenanceModalOpen(false)} title="Registra manutenzione" size="lg" data-id="element-3901">
        <div className="space-y-6" data-id="element-3902">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 mb-4" data-id="element-3903">
            <h3 className="font-bold text-seform-navy" data-id="element-3904">{selectedItem?.name}</h3>
            <p className="text-sm text-slate-500" data-id="element-3905">
              Codice: {selectedItem?.code} • Ultima manutenzione:{' '}
              {selectedItem?.lastMaintenance}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3906">
            <DatePicker label="Data esecuzione" value={maintenanceData.date || ''} onChange={e => setMaintenanceData({
            ...maintenanceData,
            date: e.target.value
          })} data-id="element-3907" />
            <Select label="Esito" value={maintenanceData.result || ''} onChange={e => setMaintenanceData({
            ...maintenanceData,
            result: e.target.value
          })} options={[{
            value: 'Positivo',
            label: 'Positivo'
          }, {
            value: 'Negativo',
            label: 'Negativo'
          }]} required data-id="element-3908" />
            <Select label="Tipo manutenzione" value={maintenanceData.type || 'Ordinaria'} onChange={e => setMaintenanceData({
            ...maintenanceData,
            type: e.target.value
          })} options={[{
            value: 'Ordinaria',
            label: 'Ordinaria'
          }, {
            value: 'Straordinaria',
            label: 'Straordinaria'
          }]} required data-id="element-3909" />
            <DatePicker label="Prossima scadenza" value={maintenanceData.nextDate || ''} onChange={e => setMaintenanceData({
            ...maintenanceData,
            nextDate: e.target.value
          })} data-id="element-3910" />
            <div className="col-span-full" data-id="element-3911">
              <Textarea label="Note / Interventi effettuati" value={maintenanceData.notes || ''} onChange={e => setMaintenanceData({
              ...maintenanceData,
              notes: e.target.value
            })} required data-id="element-3912" />
            </div>
            <div className="col-span-full flex items-center gap-2" data-id="element-3913">
              <input type="checkbox" id="maintenanceEmailReminder" className="rounded text-seform-orange focus:ring-seform-orange" checked={maintenanceData.sendEmailReminder || false} onChange={e => setMaintenanceData({
              ...maintenanceData,
              sendEmailReminder: e.target.checked
            })} data-id="element-3914" />
              <label htmlFor="maintenanceEmailReminder" className="text-sm text-slate-700 flex items-center gap-1" data-id="element-3915">
                <Mail className="h-4 w-4 text-slate-400" data-id="element-3916" />
                Invia promemoria email per prossima scadenza
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3917">
            <Button variant="flat" color="default" onClick={() => setIsMaintenanceModalOpen(false)} data-id="element-3918">
              Annulla
            </Button>
            <Button onClick={saveMaintenance} startContent={<CheckCircle2 className="h-4 w-4" data-id="element-3920" />} data-id="element-3919">
              Registra
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-3921">
        <AnimatePresence mode="wait" data-id="element-3922">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-3923">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3924">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-3925" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3926">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3927">
              <div className="space-y-6" data-id="element-3928">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-3929">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-3930" />
                  <div data-id="element-3931">
                    <p className="text-sm text-red-700 font-medium" data-id="element-3932">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-3933">
                      Il macchinario <strong data-id="element-3934">{itemToDelete?.name}</strong> verrà
                      eliminato definitivamente.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-3935">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-3936">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-3938" />} data-id="element-3937">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}